import {Typography} from '@sollapay/ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon
} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import type {FC} from 'react';

export interface ShareRoleMenuOption {
  value: string;
  label: string;
  description: string;
  disabled?: boolean;
}

export interface ShareRoleMenuAction {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

export interface ShareRoleMenuProps {
  label: string;
  personName: string;
  value: string;
  options: ShareRoleMenuOption[];
  actions: ShareRoleMenuAction[];
  readOnly: boolean;
  onValueChange?: (value: string) => void;
}

/** A person's role as a menu of the roles they can take, then the row's actions; plain text when read-only. */
export const ShareRoleMenu: FC<ShareRoleMenuProps> = ({
  label,
  personName,
  value,
  options,
  actions,
  readOnly,
  onValueChange
}) => {
  const {t} = useTranslation('trustAccounts');

  if (readOnly) {
    return (
      <Typography size="sm" color="secondary" className="ms-1 shrink-0 px-2">
        {label}
      </Typography>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        render={renderTrigger(label, t('share.roleMenu', {role: label, name: personName}))}
      />
      <DropdownMenuContent align="end" className="w-70">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={next => {
            if (next !== value) onValueChange?.(String(next));
          }}
        >
          {options.map(option => (
            <ShareRoleMenuOptionItem key={option.value} option={option} />
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        {actions.map(action => (
          <DropdownMenuItem
            key={action.label}
            variant={action.destructive ? 'destructive' : 'default'}
            onClick={action.onClick}
          >
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function renderTrigger(label: string, ariaLabel: string) {
  return (
    <Button
      variant="tertiary"
      size="sm"
      aria-label={ariaLabel}
      icon={<Icon name="chevron-down" className="size-3.5" aria-hidden="true" />}
      iconPosition="end"
      className="ms-1 h-8 px-2 font-medium text-fg-secondary"
    >
      {label}
    </Button>
  );
}

const ShareRoleMenuOptionItem: FC<{option: ShareRoleMenuOption}> = ({option}) => (
  <DropdownMenuRadioItem value={option.value} disabled={option.disabled} closeOnClick>
    <div className="flex min-w-0 flex-1 flex-col">
      <Typography size="sm" weight="semibold">
        {option.label}
      </Typography>
      <Typography size="xs" color="tertiary" className="whitespace-normal">
        {option.description}
      </Typography>
    </div>
  </DropdownMenuRadioItem>
);
